import { useEffect } from "react";
import { api } from "@/lib/api";

/**
 * Injects SEO-relevant <head> tags from /api/seo/:kind/:id into document.
 * Crawlers (Googlebot etc.) execute JS so this works for OG/Twitter cards
 * in modern SEO. For the absolute best result, ensure the build also has
 * static fallbacks in /public/index.html.
 */
export default function SeoHead({ kind, id, lang = "de", fallbackTitle = "FicoPilot" }) {
  useEffect(() => {
    let cancelled = false;
    const orig = document.title;

    function setMeta(name, value, isProperty = false) {
      if (!value) return;
      const attr = isProperty ? "property" : "name";
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    }

    function setLink(rel, href) {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    }

    if (!kind || !id) {
      document.title = fallbackTitle;
      return () => {
        document.title = orig;
      };
    }

    api
      .get(`/seo/${kind}/${encodeURIComponent(id)}`, { params: { lang } })
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        if (d.title) document.title = d.title;
        if (d.description) setMeta("description", d.description);
        // OpenGraph
        setMeta("og:title", d.title, true);
        setMeta("og:description", d.description, true);
        setMeta("og:image", d.og_image, true);
        setMeta("og:url", d.canonical, true);
        setMeta("og:type", d.type || "website", true);
        // Twitter
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", d.title);
        setMeta("twitter:description", d.description);
        setMeta("twitter:image", d.og_image);
        // Canonical
        setLink("canonical", d.canonical);
      })
      .catch(() => {
        if (!cancelled) document.title = fallbackTitle;
      });

    return () => {
      cancelled = true;
      document.title = orig;
    };
  }, [kind, id, lang, fallbackTitle]);

  return null;
}
